import React, { useState, useEffect, Dispatch, SetStateAction } from "react"
import { Form, message, Modal } from "antd"

import axios from "@/modules/shared/AxiosInterceptor"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPen } from "@fortawesome/free-solid-svg-icons"
import {
  AliasFormItem,
  PhoneFormItem,
  DeliveryNoteFormItem,
  SenderNameFormItem,
  BuildingNumberFormItem,
  AddressAutocomplete,
} from "@/UI/index"

import { formatPhoneNumber } from "@/utils/FormatPhoneNumber"

import KosmoButton from "@/UI/Buttons/KosmoButton"

import {
  openStoreEditedSuccess,
  openStoreError,
} from "@/UI/Notifications/NotificationsWrappers.component"

import { useTranslation } from "react-i18next"
import useInputPhoneNumber from "@/hooks/useInputPhoneNumber"
import { PickupDetailsType } from "@/types/createSingleOrder.types"
import { useSetRecoilState } from "recoil"
import { userAtom } from "@/atoms/userAtom"
import { EmailFormItem } from "@/UI/Forms/FormItems/EmailFormItem"
import { useGeocoding } from "@/hooks/useGeocoding"

interface EditLocationModalProps {
  store: PickupDetailsType
  iconSize?: "small"
  parentModalOpenControl?: string
  setParentModalOpenControl?: Dispatch<SetStateAction<string>>
}

// TODO: Should be merged with Create Location Form
const EditLocationModal: React.FC<EditLocationModalProps> = ({
  store,
  iconSize,
  parentModalOpenControl,
  setParentModalOpenControl,
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [fieldsChange, setFieldsChange] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)
  const [postcode, setPostcode] = useState<string | undefined>()
  const setUser = useSetRecoilState(userAtom)

  const { countryCode, handlePhoneSelect } = useInputPhoneNumber(form, "phone")
  const { geocodeByAddress } = useGeocoding()
  const [searchValue, setSearchValue] = useState("")

  const modalVisibilitySetter = (visible: boolean) => {
    if (setParentModalOpenControl) {
      setParentModalOpenControl(!!visible && store.id ? store.id : "")
    } else {
      setIsModalVisible(visible)
    }
  }

  useEffect(() => {
    if (parentModalOpenControl === store.id) {
      setIsModalVisible(true)
    } else {
      setIsModalVisible(false)
    }
  }, [parentModalOpenControl])

  useEffect(() => {
    if (isModalVisible) {
      setSearchValue(store.address)
      form.setFieldsValue({
        id: store.id,
        productType: store.productType,
        name: store.name,
        alias: store.alias,
        address: store.address,
        email: store.email,
        buildingNumber: store.buildingNumber,
        phone: formatPhoneNumber(store.phone),
        notes: store.notes,
      })
    }
  }, [isModalVisible])

  const editStore = async (formData: PickupDetailsType) => {
    setIsLoading(true)
    try {
      const geocodeResult = await geocodeByAddress(formData.address)
      if (!geocodeResult) {
        message.error(t("common.notification.invalidAddressDetails"))
        return
      }
      const { data } = await axios.put("orgs/stores", {
        id: store.id,
        name: formData.name,
        address: formData.address,
        buildingNumber: formData.buildingNumber,
        latitude: geocodeResult?.location.lat,
        longitude: geocodeResult?.location.lng,
        postalCode: postcode,
        email: formData.email,
        alias: formData.alias,
        notes: formData.notes,
        phone: formData.phone.replace(/\+/g, "").replace(/\s/g, ""),
      })
      modalVisibilitySetter(false)
      openStoreEditedSuccess(t)
      setUser((state) => {
        const storeIndex = state.stores.findIndex((store) => store.id === data.id)
        const updatedStores = [...state.stores]
        updatedStores[storeIndex] = data
        return { ...state, stores: updatedStores }
      })
    } catch (error) {
      console.log(error)
      openStoreError(t)
    } finally {
      setIsLoading(false)
    }
  }

  // TODO All handlePredictionSelection shall be refactored - forget about dynamically setting the key
  const handlePredictionSelection = async (prediction: any) => {
    const geocodeResult = await geocodeByAddress(prediction?.description)
    form.setFieldValue("address", geocodeResult?.address.label)
    setPostcode(geocodeResult?.address?.postalCode)
    setSearchValue(geocodeResult?.address?.label || prediction.description)
    form.setFieldsValue({
      address: geocodeResult?.address?.label,
    })
  }

  const onSearchChange = (e: any) => {
    setSearchValue(e.target.value)
    form.setFieldsValue({
      address: e.target.value,
    })
    setPostcode(undefined)
  }

  const iconSizeClassname = iconSize === "small" ? "w-5 h-5" : "w-8 h-8"

  return (
    <>
      <div
        className={`flex items-center rounded-full bg-gray-200 cursor-pointer ${iconSizeClassname}`}
        onClick={() => modalVisibilitySetter(true)}
      >
        <FontAwesomeIcon
          className="text-gray-400 mx-auto"
          icon={faPen}
          {...(iconSize === "small" ? { fontSize: 8 } : {})}
        />
      </div>
      <Modal
        open={isModalVisible || store.id === parentModalOpenControl}
        centered={true}
        footer={null}
        closable={false}
        destroyOnClose={true}
        zIndex={3000}
      >
        <Form
          name="business-details"
          form={form}
          onFinish={editStore}
          layout="vertical"
          onValuesChange={() => {
            setFieldsChange(true)
          }}
          className="pt-4"
          validateTrigger="onSubmit"
        >
          <AliasFormItem
            name={"alias"}
            placeholder={t("create-order.forms.locationName")}
            validationErrorText={t("create-order.forms.enterAlias")}
            withWarning
          />
          <SenderNameFormItem
            name={"name"}
            placeholder={t("create-order.forms.senderFullName")}
            label={t("create-order.forms.senderFullName")}
            validationErrorText={t("common.forms.enterName")}
          />
          <AddressAutocomplete
            name="address"
            label={t("create-order.forms.addressIncludingStreetNumber")}
            placeholder={t("create-order.forms.addressIncludingStreetNumber")}
            searchValue={searchValue}
            onSelected={handlePredictionSelection}
            onChange={onSearchChange}
          />
          <BuildingNumberFormItem
            name={"buildingNumber"}
            label={t("create-order.forms.buildingNumberPlaceholder")}
            placeholder={t("create-order.forms.buildingNumberPlaceholder")}
          />
          <PhoneFormItem
            handlePhoneSelect={handlePhoneSelect}
            countryCode={countryCode}
            skipValidation
          />
          <EmailFormItem name="email" placeholder={t("common.forms.emailAddressOptional")} />
          <DeliveryNoteFormItem
            name="notes"
            label={t("create-order.forms.pickupInstructions")}
            placeholder={t("create-order.forms.pickupInstructionsPlaceholder")}
            size="middle"
          />
        </Form>
        <div className="flex w-full justify-end mt-6 space-x-4">
          <KosmoButton
            onClick={() => modalVisibilitySetter(false)}
            classname="font-semibold"
            isDisabled={isLoading}
            isLoading={isLoading}
          >
            {t("common.cancel")}
          </KosmoButton>
          <div className="ml-2">
            <KosmoButton
              type="primary"
              onClick={() => form.submit()}
              classname="font-semibold"
              isDisabled={!fieldsChange || isLoading}
              isLoading={isLoading}
            >
              {t("profile.GeneralSettings.generalSettings.saveChanges")}
            </KosmoButton>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default EditLocationModal
